/**
 * Pricing API — Connect RPC implementation
 * Calls ProjectDataService via sabaody gateway.
 */ import { projectDataClient } from '../connect/clients';
import { proposalProjectApiConnect } from './proposalProjectApi.connect';
function toPlainObject(raw) {
    if (!raw) return {};
    if (typeof raw === 'object' && typeof raw.toJson === 'function') {
        try {
            const json = raw.toJson();
            if (json && typeof json === 'object') {
                return json;
            }
        } catch  {
        // Fall back to direct object access.
        }
    }
    return raw ?? {};
}
function readField(obj, ...keys) {
    for (const key of keys){
        if (obj[key] !== undefined) return obj[key];
    }
    return undefined;
}
function toIsoString(value) {
    if (!value) return '';
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? '' : value.toISOString();
    }
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString();
    }
    if (typeof value === 'object') {
        const ts = value;
        if (ts.seconds != null) {
            const seconds = typeof ts.seconds === 'bigint' ? Number(ts.seconds) : Number(ts.seconds);
            const nanos = Number(ts.nanos ?? 0);
            if (Number.isFinite(seconds) && Number.isFinite(nanos)) {
                const d = new Date(seconds * 1000 + Math.floor(nanos / 1000000));
                return Number.isNaN(d.getTime()) ? '' : d.toISOString();
            }
        }
    }
    return '';
}
function parseJsonObject(value) {
    if (typeof value !== 'string' || !value.trim()) return {};
    try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed;
        }
    } catch  {
    // Keep API resilient to malformed or legacy payloads.
    }
    return {};
}
function parseJsonArray(value) {
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch  {
        return [];
    }
}
function mapScenarioSummary(raw) {
    const scenario = toPlainObject(raw);
    return {
        id: String(readField(scenario, 'id', 'pricingId', 'scenarioId') ?? ''),
        projectId: String(readField(scenario, 'projectId', 'project_id') ?? ''),
        scenarioName: String(readField(scenario, 'scenarioName', 'scenario_name') ?? ''),
        isPrimary: Boolean(readField(scenario, 'isPrimary', 'is_primary')),
        createdAt: toIsoString(readField(scenario, 'createdAt', 'created_at')),
        updatedAt: toIsoString(readField(scenario, 'updatedAt', 'updated_at'))
    };
}
function mapScenarioDetail(raw) {
    const scenario = toPlainObject(raw);
    const summary = mapScenarioSummary(scenario);
    const parsedData = parseJsonObject(readField(scenario, 'data'));
    const rowIsPrimaryValue = readField(scenario, 'isPrimary', 'is_primary');
    const rowIsPrimary = typeof rowIsPrimaryValue === 'boolean' ? rowIsPrimaryValue : undefined;
    const parsedCreatedAt = typeof parsedData.createdAt === 'string' ? parsedData.createdAt : toIsoString(parsedData.createdAt);
    const parsedUpdatedAt = typeof parsedData.updatedAt === 'string' ? parsedData.updatedAt : toIsoString(parsedData.updatedAt);
    return {
        ...parsedData,
        id: summary.id || String(parsedData.id ?? ''),
        projectId: summary.projectId || String(parsedData.projectId ?? ''),
        scenarioName: summary.scenarioName || String(parsedData.scenarioName ?? ''),
        isPrimary: rowIsPrimary ?? Boolean(parsedData.isPrimary),
        companyId: String(parsedData.companyId ?? ''),
        createdAt: summary.createdAt || parsedCreatedAt || '',
        updatedAt: summary.updatedAt || parsedUpdatedAt || ''
    };
}
function mapIndirectRateConfig(raw) {
    const cfg = toPlainObject(raw);
    return {
        id: String(readField(cfg, 'id', 'configId') ?? ''),
        companyId: String(readField(cfg, 'companyId', 'company_id') ?? ''),
        name: String(readField(cfg, 'name') ?? ''),
        isDefault: Boolean(readField(cfg, 'isDefault', 'is_default')),
        pools: parseJsonArray(readField(cfg, 'pools')),
        gaBaseMethod: String(readField(cfg, 'gaBaseMethod', 'ga_base_method') ?? ''),
        useMultiplicative: Boolean(readField(cfg, 'useMultiplicative', 'use_multiplicative')),
        createdAt: toIsoString(readField(cfg, 'createdAt', 'created_at')),
        updatedAt: toIsoString(readField(cfg, 'updatedAt', 'updated_at'))
    };
}
// Source-of-truth alignment for LCAT base rates.
//
// Pricing scenarios used to store their own copy of each LCAT's base
// hourly rate inside rateCard.categories[].rates, independent of the
// canonical labor_categories.hourly_rate column. Edits on the LCAT tab
// never propagated to the rate card, and edits in the Pricing UI never
// propagated back to the LCAT tab — the LCAT/CLIN tab stats stayed stale.
//
// Now: labor_categories.hourly_rate is the one place a base rate lives.
// Every GetPricing response gets its rate-card categories overlaid with
// the canonical row so the rate the Pricing UI shows always matches the
// rate the LCAT tab shows. Scenario-specific layers (escalation, vehicle
// discounts, fee methods) continue to live on the scenario blob — only
// the base is unified.
//
// Best effort: a missing/erroring LCAT lookup leaves the rate card as-is
// rather than failing the whole pricing fetch. The user still sees
// scenario data; the cross-surface inconsistency just lingers.
//
// TODO(follow-up): this lives below react-query so it bypasses the
// useLCATs cache and forces an extra HTTP round-trip every getPricing
// call, even when useLCATs(projectId) is already warm in the same tab.
// Cheap fix: add an in-flight dedupe / short-TTL cache scoped to
// projectId here, or move the overlay up into a usePricing-derived
// hook that can read the shared queryClient. Out of scope for the
// initial LCAT-pricing P2 fix.
async function overlayCanonicalLCATRates(projectId, pricing) {
    if (!pricing.rateCard || pricing.rateCard.categories.length === 0) {
        return pricing;
    }
    let canonical;
    try {
        const lcatsResp = await proposalProjectApiConnect.getLCATs(projectId);
        canonical = lcatsResp.laborCategories;
    } catch (err) {
        // The original P2 presented as "looks saved but isn't". A silent
        // fallback here would resurrect that exact UX whenever an LCAT
        // fetch fails. Log loudly enough that a regression surfaces in
        // the dev console while still degrading gracefully on the wire.
        // eslint-disable-next-line no-console
        console.warn('[pricingApi.overlay] failed to load LCATs for overlay', {
            projectId,
            err
        });
        return pricing;
    }
    const ratesByLcatId = new Map();
    for (const lcat of canonical){
        if (typeof lcat.baseHourlyRate === 'number' && lcat.baseHourlyRate > 0) {
            ratesByLcatId.set(lcat.code, lcat.baseHourlyRate);
        }
    }
    const overlaid = {
        ...pricing,
        rateCard: {
            ...pricing.rateCard,
            categories: pricing.rateCard.categories.map((category)=>{
                const canonicalRate = ratesByLcatId.get(category.laborCategoryId);
                if (canonicalRate === undefined) return category;
                // Only the base rate is sourced from the canonical LCAT row.
                // The seeded shape uses { default: {...} } for that base; older
                // exports used "base". Future categories may carry additional
                // parameters (location variants, period-specific overrides,
                // ceilings stored alongside base) — those keep their own values
                // and live in the scenario blob, NOT on the LCAT row. We
                // explicitly enumerate the base-rate keys here so adding a new
                // parameter never silently inherits the canonical base rate.
                const baseRateKeys = [
                    'default',
                    'base'
                ];
                const existingRates = category.rates ?? {};
                const updatedRates = {
                    ...existingRates
                };
                let patched = false;
                for (const key of baseRateKeys){
                    const existing = existingRates[key];
                    if (!existing || typeof existing !== 'object') continue;
                    updatedRates[key] = {
                        ...existing,
                        value: canonicalRate
                    };
                    patched = true;
                }
                if (!patched) {
                    // No existing base-rate parameter — synthesize one under the
                    // canonical key so the UI has something to render. `source:
                    // 'default'` is the IPricingParameter enum's "we filled this
                    // in" sentinel.
                    updatedRates.default = {
                        name: 'rate',
                        label: 'Base Hourly Rate',
                        paramType: 'rate',
                        source: 'default',
                        value: canonicalRate,
                        perPeriod: false
                    };
                }
                return {
                    ...category,
                    rates: updatedRates
                };
            })
        }
    };
    return overlaid;
}
// Write-back companion to overlayCanonicalLCATRates. When the Pricing UI
// updates a rate card, any change to a category's base rate must land on
// the canonical labor_categories.hourly_rate column — otherwise the next
// getPricing overlay would happily wipe the user's edit by replacing it
// with the (old) canonical value, and the LCAT tab would never see the
// change.
//
// Each category's "default" rate parameter is the authoritative base
// rate; if the schema later evolves to multiple keys (location variants,
// period overrides), only the base one writes back here. Scenario-only
// layers (escalation, fee, vehicle adjustments) stay in the scenario
// blob and aren't sent to UpdateLCAT.
//
// Errors are swallowed per category so a transient LCAT update failure
// doesn't block the rest of the rate-card propagation. The read-side
// overlay self-heals on the next getPricing if a writeback was lost.
async function propagateRateCardToLCATs(projectId, rateCard) {
    if (!rateCard || rateCard.categories.length === 0) return;
    await Promise.all(rateCard.categories.map(async (category)=>{
        const baseParam = category.rates?.default ?? category.rates?.base;
        const value = baseParam && typeof baseParam === 'object' ? baseParam.value : undefined;
        if (typeof value !== 'number' || value <= 0) return;
        try {
            await proposalProjectApiConnect.updateLCAT(projectId, category.laborCategoryId, {
                baseHourlyRate: value
            });
        } catch (err) {
            // Best-effort writeback — the next getPricing overlay self-heals.
            // Log so a regression here doesn't reproduce the silent-save UX
            // the rest of this branch fixes.
            // eslint-disable-next-line no-console
            console.warn('[pricingApi.writeback] failed to push rate to canonical LCAT row', {
                projectId,
                lcatId: category.laborCategoryId,
                err
            });
        }
    }));
}
export const pricingApiConnect = {
    async listPricing (projectId) {
        const response = await projectDataClient.listPricing({
            projectId
        });
        return {
            scenarios: (response.scenarios ?? []).map((scenario)=>mapScenarioSummary(scenario))
        };
    },
    async getPricing (projectId, pricingId) {
        try {
            const response = await projectDataClient.getPricing({
                projectId,
                pricingId
            });
            const pricing = mapScenarioDetail(response.scenario);
            return {
                pricing: await overlayCanonicalLCATRates(projectId, pricing)
            };
        } catch (error) {
            // Fallback: list includes full scenario payloads in this service contract.
            // If direct lookup fails due legacy ID drift, recover from list response.
            const listResponse = await projectDataClient.listPricing({
                projectId
            });
            const scenarios = listResponse.scenarios ?? [];
            const found = scenarios.find((s)=>mapScenarioSummary(s).id === pricingId);
            if (found) {
                const pricing = mapScenarioDetail(found);
                return {
                    pricing: await overlayCanonicalLCATRates(projectId, pricing)
                };
            }
            throw error;
        }
    },
    async createPricing (projectId, body) {
        const response = await projectDataClient.createPricing({
            projectId,
            scenarioName: body.scenarioName ?? '',
            seedFromExtractions: body.seedFromExtractions ?? false,
            indirectRateConfigId: body.indirectRateConfigId ?? ''
        });
        return {
            pricing: mapScenarioSummary(response.scenario)
        };
    },
    async updatePricing (projectId, pricingId, body) {
        const response = await projectDataClient.updatePricing({
            projectId,
            pricingId,
            data: JSON.stringify(body)
        });
        // Rate-card edits propagate back to the canonical LCAT row so the
        // LCAT tab and any other surface see the new base rate. Done after
        // the scenario write so the user's primary action commits even if a
        // writeback fails — the next getPricing overlay will pull the stale
        // LCAT value and self-heal on the read side.
        await propagateRateCardToLCATs(projectId, body.rateCard);
        return {
            pricing: mapScenarioSummary(response.scenario)
        };
    },
    async deletePricing (projectId, pricingId) {
        await projectDataClient.deletePricing({
            projectId,
            pricingId
        });
        return {
            success: true
        };
    },
    async listIndirectRates () {
        const response = await projectDataClient.listIndirectRates({});
        return {
            configs: (response.configs ?? []).map((config)=>mapIndirectRateConfig(config))
        };
    },
    async createIndirectRate (body) {
        const response = await projectDataClient.createIndirectRate({
            name: body.name ?? '',
            isDefault: body.isDefault ?? false,
            pools: JSON.stringify(body.pools ?? []),
            gaBaseMethod: body.gaBaseMethod ?? '',
            useMultiplicative: body.useMultiplicative ?? false
        });
        return {
            config: mapIndirectRateConfig(response.config)
        };
    },
    async updateIndirectRate (configId, body) {
        const response = await projectDataClient.updateIndirectRate({
            configId,
            name: body.name ?? '',
            isDefault: body.isDefault ?? false,
            pools: body.pools ? JSON.stringify(body.pools) : '',
            gaBaseMethod: body.gaBaseMethod ?? '',
            useMultiplicative: body.useMultiplicative ?? false
        });
        return {
            config: mapIndirectRateConfig(response.config)
        };
    },
    async deleteIndirectRate (configId) {
        await projectDataClient.deleteIndirectRate({
            configId
        });
        return {
            success: true
        };
    }
};
